import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  ArrowsClockwise,
  CalendarBlank,
  ChartBar,
  Crown,
  MapPin,
  PencilSimple,
  ShieldCheck,
  Star,
  User,
} from '@phosphor-icons/react';
import { RowValue, SettingCard, SettingRow } from '../../../../../../components/settings/setting-card';
import { ValueEditorDialog, type ValueEditor } from '../../../../../../components/settings/value-editor-dialog';
import { StatusTag } from '../../../../../../components/ui/status-tag';
import { Toggle } from '../../../../../../components/ui/toggle';
import { WEEKDAYS, type Weekday } from '../../../../../../lib/staff-prefs';
import type { SchedulingRules } from '@mybusinessmate/schema';
import { Skeleton } from '../../../../../../components/ui/skeleton';
import { useSectionPatch } from '../../../../../../lib/workspace-api';
import { cx } from '../../../../../../lib/cx';
import { Button } from '../../../../../../components/ui/button';
import { Dialog } from '../../../../../../components/ui/dialog';

export const Route = createFileRoute('/subdomain/admin/_staff/settings/workspace/scheduling')({
  component: SchedulingPage,
});

type NumberField =
  | 'leadMaxActiveTasks'
  | 'specialistMaxActiveTasks'
  | 'earliestWorkingDays'
  | 'reschedulesPerMeeting'
  | 'monthlyReviewsPerCycle'
  | 'freeChaiPerProspect';

const NUMBERS: Record<NumberField, { title: string; label: string; min: number; max: number; unit: string }> = {
  leadMaxActiveTasks: {
    title: 'Max active tasks · Lead specialist',
    label: 'Tasks',
    min: 1,
    max: 30,
    unit: 'Dispatch warns when someone goes over.',
  },
  specialistMaxActiveTasks: {
    title: 'Max active tasks · Specialist',
    label: 'Tasks',
    min: 1,
    max: 30,
    unit: 'Dispatch warns when someone goes over.',
  },
  earliestWorkingDays: {
    title: 'Earliest date a client can pick',
    label: 'Working days ahead',
    min: 0,
    max: 14,
    unit: 'Counted in working days from today.',
  },
  reschedulesPerMeeting: {
    title: 'Reschedules a client can make',
    label: 'Per meeting',
    min: 0,
    max: 5,
    unit: 'Staff can always propose a new date.',
  },
  monthlyReviewsPerCycle: {
    title: 'Monthly review',
    label: 'Per billing cycle',
    min: 0,
    max: 4,
    unit: 'Plus and Pro plans.',
  },
  freeChaiPerProspect: {
    title: 'Free Chai per prospect',
    label: 'Intro chats',
    min: 0,
    max: 5,
    unit: 'Intro chats with each new prospect.',
  },
};

const PLAN_CHOICES = ['Pro only', 'Plus and Pro', 'Everyone'] as const;

function SchedulingPage() {
  const { data: rules, patch } = useSectionPatch('scheduling');
  if (!rules) return <Skeleton className="h-(--mbm-layout-dialog-wide-width) rounded-(--mbm-radius-lg)" />;
  return <SchedulingForm rules={rules} patch={patch} />;
}

function SchedulingForm({
  rules,
  patch,
}: {
  rules: SchedulingRules;
  patch: (changes: Partial<SchedulingRules>) => void;
}) {
  const [editing, setEditing] = useState<NumberField | 'strategyCallPlan' | 'freeChaiVenue' | 'days'>();

  const active:
    { title: string; label: string; editor: ValueEditor; onSave: (v: string | number) => void } | undefined =
    editing === undefined || editing === 'days'
      ? undefined
      : editing === 'strategyCallPlan'
        ? {
            title: 'Strategy call',
            label: 'Who can book one',
            editor: {
              kind: 'select',
              value: rules.strategyCallPlan,
              options: PLAN_CHOICES.map((p) => ({ value: p, label: p })),
            },
            onSave: (v) => patch({ strategyCallPlan: v as SchedulingRules['strategyCallPlan'] }),
          }
        : editing === 'freeChaiVenue'
          ? {
              title: 'Free Chai venue',
              label: 'Venue',
              editor: {
                kind: 'text',
                value: rules.freeChaiVenue,
                placeholder: 'Moguls Court, Basheerbagh',
                maxLength: 120,
              },
              onSave: (v) => patch({ freeChaiVenue: String(v) }),
            }
          : {
              title: NUMBERS[editing].title,
              label: NUMBERS[editing].label,
              editor: {
                kind: 'number',
                value: rules[editing],
                min: NUMBERS[editing].min,
                max: NUMBERS[editing].max,
                unit: NUMBERS[editing].unit,
              },
              onSave: (v) => patch({ [editing]: Number(v) }),
            };

  const dayLabel = (() => {
    const ids = WEEKDAYS.filter((d) => rules.pickableDays.includes(d.id));
    const first = ids[0];
    const last = ids.at(-1);
    return ids.length === 0 ? 'No days' : ids.length === 1 ? first!.label : `${first!.label} – ${last!.label}`;
  })();

  return (
    <>
      <SettingCard
        title="Workload"
        subtitle="Defaults for how much work each role can hold at once. You can override it per person in Staff."
      >
        <SettingRow
          icon={ChartBar}
          title="Max active tasks · Lead specialist"
          trailing={<RowValue>{rules.leadMaxActiveTasks}</RowValue>}
          onClick={() => setEditing('leadMaxActiveTasks')}
        />
        <SettingRow
          icon={ChartBar}
          title="Max active tasks · Specialist"
          trailing={<RowValue>{rules.specialistMaxActiveTasks}</RowValue>}
          onClick={() => setEditing('specialistMaxActiveTasks')}
        />
        <SettingRow
          icon={ShieldCheck}
          title="Warn before going over the limit"
          subtitle="Dispatch shows a warning. Admins can still assign."
          trailing={
            <Toggle
              aria-label="Warn before going over the limit"
              checked={rules.warnBeforeLimit}
              onCheckedChange={(on) => patch({ warnBeforeLimit: on })}
            />
          }
        />
      </SettingCard>

      <SettingCard title="Meetings" subtitle="Clients pick a date. Your team sets the exact time when approving.">
        <SettingRow
          icon={CalendarBlank}
          title="Earliest date a client can pick"
          trailing={<RowValue>{rules.earliestWorkingDays} working days ahead</RowValue>}
          onClick={() => setEditing('earliestWorkingDays')}
        />
        <SettingRow
          icon={CalendarBlank}
          title="Days clients can pick"
          subtitle="Holidays you add in Bookings are blocked too"
          trailing={<RowValue>{dayLabel}</RowValue>}
          onClick={() => setEditing('days')}
        />
        <SettingRow
          icon={ArrowsClockwise}
          title="Reschedules a client can make"
          trailing={<RowValue>{rules.reschedulesPerMeeting} per meeting</RowValue>}
          onClick={() => setEditing('reschedulesPerMeeting')}
        />
        <SettingRow
          icon={PencilSimple}
          title="Staff must give a reason when proposing a new date"
          subtitle="Shown to the client with the new date"
          trailing={<StatusTag label="Always" tone="green" dot />}
        />
        <SettingRow
          icon={Star}
          title="Monthly review"
          subtitle="Plus and Pro plans"
          trailing={<RowValue>{rules.monthlyReviewsPerCycle} per billing cycle</RowValue>}
          onClick={() => setEditing('monthlyReviewsPerCycle')}
        />
        <SettingRow
          icon={Crown}
          title="Strategy call"
          trailing={<RowValue>{rules.strategyCallPlan}</RowValue>}
          onClick={() => setEditing('strategyCallPlan')}
        />
      </SettingCard>

      <SettingCard title="Free Chai" subtitle="Intro chats with new prospects.">
        <SettingRow
          icon={User}
          title="Free Chai per prospect"
          trailing={<RowValue>{rules.freeChaiPerProspect}</RowValue>}
          onClick={() => setEditing('freeChaiPerProspect')}
        />
        <SettingRow
          icon={MapPin}
          title="Venue"
          trailing={<RowValue>{rules.freeChaiVenue || 'Not set'}</RowValue>}
          onClick={() => setEditing('freeChaiVenue')}
        />
      </SettingCard>

      {active ? (
        <ValueEditorDialog
          open
          onClose={() => setEditing(undefined)}
          title={active.title}
          label={active.label}
          editor={active.editor}
          onSave={active.onSave}
        />
      ) : null}
      <Dialog
        open={editing === 'days'}
        onClose={() => setEditing(undefined)}
        title="Days clients can pick"
        description="Meetings can only be requested for these days."
      >
        {editing === 'days' ? (
          <div className="flex flex-col gap-(--mbm-space-5)">
            <div role="group" aria-label="Days clients can pick" className="flex flex-wrap gap-(--mbm-space-2)">
              {WEEKDAYS.map((d) => {
                const on = rules.pickableDays.includes(d.id);
                return (
                  <button
                    key={d.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => {
                      const next: Weekday[] = on
                        ? rules.pickableDays.filter((x) => x !== d.id)
                        : [...rules.pickableDays, d.id];
                      patch({ pickableDays: WEEKDAYS.map((w) => w.id).filter((id) => next.includes(id)) });
                    }}
                    className={cx(
                      'h-(--mbm-size-button-row) rounded-full border px-(--mbm-space-4) text-label-medium focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)',
                      on
                        ? 'border-action-primary bg-action-primary text-text-on-dark'
                        : 'border-border-default bg-surface-card text-text-secondary',
                    )}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
            <Button size="compact" onClick={() => setEditing(undefined)}>
              Done
            </Button>
          </div>
        ) : null}
      </Dialog>
    </>
  );
}
